import React from 'react';
import { createPortal } from 'react-dom';
import { Tooltip, Paper, Box, Badge, Typography, Select, MenuItem, FormControl, Popover } from '@mui/material';
import { useMap, useMapEvents } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import L from 'leaflet';
import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';
import { selectPillarsWithAccountingsFiltered } from '../../store/selectors/supervisionSelector';
import { pillarIcon, projectStatusOreolIcon } from '../layers/icons';
import { aimToObject } from './helpers';
import { $authHost } from '../../http';
import {
    CloudOff as CloudOffIcon,
    MyLocation as MyLocationIcon,
    GpsFixed as GpsFixedIcon,
    Schedule as ScheduleIcon,
    Explore as ExploreIcon,
    TouchApp as TouchAppIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    List as ListIcon,
    Engineering as EngineeringIcon
} from '@mui/icons-material';

const WORK_PILLAR_LOG_QUEUE_KEY = 'workPillarLogQueue';

// Добавляем CSS для анимации pulse и progress
const pulseAnimation = `
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 165, 0, 0.5);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 165, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 165, 0, 0);
  }
}

@keyframes longPressGlow {
  0% {
    box-shadow: 0px 2px 6px grey;
  }
  100% {
    box-shadow: 0px 0px 50px 25px rgba(34, 181, 71, 0.8);
  }
}

.long-press-animation {
  animation: longPressGlow 2s ease-out;
}
`;

// Добавляем стили в head
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = pulseAnimation;
    document.head.appendChild(style);
}

// Компонент плашки опоры
const PillarCard = ({ pillar, status, distance, timestamp, onConfirm, onReject, onClick, onLongPress, isActive, isPrimary = true, onForceSelector }) => {
    const formatTime = useCallback((timestamp) => {
        return new Date(timestamp).toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }, []);

    const getBackgroundColor = () => {
        switch (status) {
            case 'pending': return 'rgba(33, 150, 243, 0.9)'; // Голубой
            case 'confirmed': return 'rgba(76, 175, 80, 0.9)'; // Зеленый
            case 'inactive': return 'rgba(158, 158, 158, 0.9)'; // Серый
            default: return 'rgba(40, 40, 40, 0.9)';
        }
    };

    const getBorderColor = () => {
        switch (status) {
            case 'pending': return '#2196F3';
            case 'confirmed': return '#4CAF50';
            case 'inactive': return '#9E9E9E';
            default: return 'transparent';
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: '150px',
                maxWidth: '300px',
                bgcolor: getBackgroundColor(),
                border: `2px solid ${getBorderColor()}`,
                borderRadius: 1,
                p: { xs: 0.1, md: 0.2 },
                mr: 0.5,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                opacity: isActive ? 1 : (isPrimary ? 0.95 : 0.8),
                transform: isActive ? 'scale(1)' : (isPrimary ? 'scale(0.98)' : 'scale(0.92)'),
                '&:hover': {
                    transform: isActive ? 'scale(1.01)' : (isPrimary ? 'scale(1.0)' : 'scale(0.95)'),
                    opacity: isActive ? 1 : (isPrimary ? 1 : 0.9),
                }
            }}
            onClick={onClick}
            onContextMenu={onLongPress}
        >
            {/* Иконка опоры */}
            <Box sx={{
                width: { xs: 24, md: 28 },
                height: { xs: 24, md: 28 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: { xs: 0.2, md: 1 }
            }}>
                {/* {pillarIcon(
                    pillar.type,
                    pillar.status,
                    pillar.material_type,
                    pillar.supports_count,
                    window.innerWidth < 768 ? 12 : 14
                )} */}

                <Box sx={{ position: 'relative', width: { xs: 24, md: 28 }, height: { xs: 24, md: 28 } }}>
                    <Box style={{ position: 'absolute', margin: window.innerWidth < 768 ? '2px 0 0 7px' : '6px 0 0 8px' }}>
                        {pillarIcon(
                            pillar.type,
                            pillar.status,
                            pillar.material_type,
                            pillar.supports_count,
                            window.innerWidth < 768 ? 10 : 12
                        )}
                    </Box>
                    {[2, 3, 4, 7].includes(Number(pillar?.layer)) ? (
                        <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                            {projectStatusOreolIcon('pillar', Number(pillar?.layer), window.innerWidth < 768 ? 24 : 28)}
                        </Box>
                    ) : null}
                </Box>
            </Box>

            {/* Информация об опоре */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                    fontSize: isPrimary ? { xs: '0.7rem', md: '0.75rem' } : { xs: '0.65rem', md: '0.7rem' },
                    fontWeight: isPrimary ? 600 : 500,
                    color: 'white',
                    lineHeight: 1.1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {pillar.name || `Опора ${pillar.fid}`}
                </Typography>
                <Typography sx={{
                    fontSize: isPrimary ? { xs: '0.65rem', md: '0.7rem' } : { xs: '0.6rem', md: '0.65rem' },
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.1
                }}>
                    {distance ? `${distance.toFixed(1)}м` : ''} • {formatTime(timestamp)}
                    {/* {!isPrimary && status === 'inactive' && (
                        <Typography component="span" sx={{
                            fontSize: '0.6rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            ml: 0.5,
                            fontStyle: 'italic'
                        }}>
                            • клик для активации
                        </Typography>
                    )} */}
                </Typography>
            </Box>

            {/* Кнопки подтверждения/отклонения для pending статуса */}
            {status === 'pending' && (
                <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5 }}>
                    <Box
                        sx={{
                            width: { xs: 20, md: 24 },
                            height: { xs: 20, md: 24 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(76, 175, 80, 0.9)',
                            border: '1px solid white',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                                bgcolor: 'rgba(76, 175, 80, 1)',
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onConfirm && onConfirm();
                        }}
                    >
                        <CheckIcon sx={{ fontSize: { xs: 14, md: 18 }, color: 'white' }} />
                    </Box>
                    <Box
                        sx={{
                            width: { xs: 20, md: 24 },
                            height: { xs: 20, md: 24 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(244, 67, 54, 0.9)',
                            borderRadius: '50%',
                            border: '1px solid white',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                                bgcolor: 'rgba(244, 67, 54, 1)',
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onReject && onReject(e);
                        }}
                    >
                        <CloseIcon sx={{ fontSize: { xs: 14, md: 18 }, color: 'white' }} />
                    </Box>
                </Box>
            )}

            {/* Кнопка принудительного открытия списка опор для активной опоры */}
            {status === 'confirmed' && isActive && onForceSelector && (
                <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5 }}>
                    <Box
                        sx={{
                            width: { xs: 20, md: 24 },
                            height: { xs: 20, md: 24 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(33, 150, 243, 0.9)',
                            border: '1px solid white',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                                bgcolor: 'rgba(33, 150, 243, 1)',
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onForceSelector && onForceSelector(e);
                        }}
                    >
                        <ListIcon sx={{ fontSize: { xs: 14, md: 18 }, color: 'white' }} />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

// Компонент селектора опор
const PillarSelector = ({ getSelectorPillars, onSelect, onClose }) => {
    const { nearby, within100m } = getSelectorPillars();

    const formatTime = useCallback((timestamp) => {
        return new Date(timestamp).toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }, []);

    const renderPillarItem = (item, index) => (
        <Box
            key={item.pillar.fid}
            sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: { xs: 0.5, md: 1 },
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onClick={() => onSelect(item.pillar)}
        >
            {/* Иконка опоры */}
            {/* <Box sx={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1
            }}>
                {pillarIcon(
                    item.pillar.type,
                    item.pillar.status,
                    item.pillar.material_type,
                    item.pillar.supports_count,
                    16
                )}
            </Box> */}

            <Box sx={{ position: 'relative', width: { xs: 24, md: 28 }, height: { xs: 24, md: 28 } }}>
                <Box style={{ position: 'absolute', margin: window.innerWidth < 768 ? '2px 0 0 7px' : '6px 0 0 8px' }}>
                    {pillarIcon(
                        item.pillar.type,
                        item.pillar.status,
                        item.pillar.material_type,
                        item.pillar.supports_count,
                        window.innerWidth < 768 ? 10 : 12
                    )}
                </Box>
                {[2, 3, 4, 7].includes(Number(item.pillar?.layer)) ? (
                    <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                        {projectStatusOreolIcon('pillar', Number(item.pillar?.layer), window.innerWidth < 768 ? 24 : 28)}
                    </Box>
                ) : null}
            </Box>

            {/* Информация об опоре */}
            <Box sx={{ flex: 1 }}>
                <Typography sx={{
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    fontWeight: 500,
                    color: 'white',
                    lineHeight: 1.1
                }}>
                    {item.pillar.name || `Опора ${item.pillar.fid}`}
                </Typography>
                <Typography sx={{
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.1
                }}>
                    {item.distance.toFixed(1)}м
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ width: '100%', maxHeight: 400, overflow: 'auto' }}>
            {/* Заголовок */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '2px 5px',
                borderBottom: '2px solid rgba(255, 255, 255, 0.2)'
            }}>
                <Typography sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, fontWeight: 600, color: 'white' }}>
                    Выберите опору
                </Typography>
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: '50%',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                        }
                    }}
                    onClick={onClose}
                >
                    <CloseIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
            </Box>

            {/* Секция "Найдено рядом" */}
            {nearby.length > 0 && (
                <Box>
                    <Typography sx={{
                        fontSize: { xs: '0.75rem', md: '0.8rem' },
                        fontWeight: 600,
                        color: '#90caf9',
                        padding: '2px 5px',
                        bgcolor: 'rgba(144, 202, 249, 0.1)',
                        borderBottom: '1px solid rgba(144, 202, 249, 0.3)'
                    }}>
                        Найдено рядом ({nearby.length})
                    </Typography>
                    {nearby.map((item, index) => renderPillarItem(item, index))}
                </Box>
            )}

            {/* Секция "В радиусе 100м" */}
            {within100m.length > 0 && (
                <Box>
                    <Typography sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#ff9800',
                        p: 1,
                        bgcolor: 'rgba(255, 152, 0, 0.1)',
                        borderBottom: '1px solid rgba(255, 152, 0, 0.3)'
                    }}>
                        В радиусе 100м ({within100m.length})
                    </Typography>
                    {within100m.map((item, index) => renderPillarItem(item, index))}
                </Box>
            )}

            {/* Если нет опор */}
            {nearby.length === 0 && within100m.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                        Нет доступных опор поблизости
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

const LocationControl = () => {
    const map = useMap()
    const [enabled, setEnabled] = useState(false)
    const buttonRef = useRef(null);
    const [location, setLocation] = useState(null)
    const [error, setError] = useState(null)
    const [orientation, setOrientation] = useState(null);
    const pillars = useSelector(selectPillarsWithAccountingsFiltered)
    const user = useSelector(state => state.state.user)
    const marker = useRef(null)
    const markerAccuracy = useRef(null)
    const [workPillar, setWorkPillar] = useState(null)
    const [workPillarError, setWorkPillarError] = useState(false)

    //В новой логике оринентиром будет эта переменная, которая будет хранить массивы найденных опор в зависимости от времени опроса
    //При периодичности 10сек будем держать активными три массива
    //И при каждом имзменении вычислять опоры которые есть во всех трех массивах и сортировать по близости
    const [locatedPillars, setLocatedPillars] = useState([])
    const locatedPillarsRef = useRef([])
    const locatedPillarsTimeoutRef = useRef()

    // Новые состояния для системы плашек
    const [proposedPillar, setProposedPillar] = useState(null) // Предлагаемая опора (с голубым фоном)
    const [currentPillar, setCurrentPillar] = useState(null) // Текущая активная опора (с зеленым фоном)
    const [visitedPillars, setVisitedPillars] = useState([]) // История посещенных опор (серый фон)
    const [showPillarSelector, setShowPillarSelector] = useState(false) // Показывать ли селектор опор
    const [selectorAnchorEl, setSelectorAnchorEl] = useState(null) // Элемент для привязки селектора

    // Референсы для маркеров индикации опор на карте
    const pillarMarkersRef = useRef({
        proposed: null,
        current: null,
        visited: []
    })

    // Функция для вычисления расстояния между двумя точками в метрах
    const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
        const R = 6371e3; // радиус Земли в метрах
        const φ1 = lat1 * Math.PI / 180; // φ, λ в радианах
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c; // в метрах
    }, []);

    // Функция обработки найденных опор рядом
    const processNearbyPillars = useCallback((nearbyPillars) => {
        if (nearbyPillars.length === 0) return;

        const closestPillar = nearbyPillars[0];

        // Если это первая опора и нет текущей опоры
        if (!currentPillar) {
            setProposedPillar({
                pillar: closestPillar.pillar,
                distance: closestPillar.distance,
                timestamp: Date.now(),
                status: 'pending'
            });
            return;
        }

        // Если есть текущая опора, проверяем нужно ли предлагать новую
        if (currentPillar && closestPillar.pillar.fid !== currentPillar.pillar.fid) {
            // const currentDistance = calculateDistance(
            //     location.latitude,
            //     location.longitude,
            //     currentPillar.pillar.latitude,
            //     currentPillar.pillar.longitude
            // );
            const currentDistance = new L.latLng(new L.latLng(currentPillar.pillar.coords[0], currentPillar.pillar.coords[1])).distanceTo(location.latlng)

            // Предлагаем новую опору если она на 5+ метров ближе
            if (closestPillar.distance < currentDistance - 5) {
                setProposedPillar({
                    pillar: closestPillar.pillar,
                    distance: closestPillar.distance,
                    timestamp: Date.now(),
                    status: 'pending'
                });
            }
        }
    }, [currentPillar, location]);

    // Функция подтверждения предлагаемой опоры
    const confirmProposedPillar = useCallback(() => {
        if (!proposedPillar) return;

        // Если есть текущая опора, перемещаем её в историю
        if (currentPillar) {
            // Проверяем, нет ли уже этой опоры в истории
            const existingIndex = visitedPillars.findIndex(p => p.pillar.fid === currentPillar.pillar.fid);
            if (existingIndex >= 0) {
                // Обновляем существующую запись
                const updatedVisited = [...visitedPillars];
                updatedVisited[existingIndex] = currentPillar;
                setVisitedPillars(updatedVisited);
            } else {
                // Добавляем в историю
                setVisitedPillars(prev => [currentPillar, ...prev]);
            }
        }

        // Делаем предлагаемую опору текущей
        const newCurrentPillar = {
            ...proposedPillar,
            status: 'confirmed'
        };

        setCurrentPillar(newCurrentPillar);
        setWorkPillar({
            pillar: proposedPillar.pillar,
            time: proposedPillar.timestamp
        });
        setProposedPillar(null);
    }, [proposedPillar, currentPillar, visitedPillars]);

    // Функция отклонения предлагаемой опоры
    const rejectProposedPillar = useCallback((event) => {
        if (!proposedPillar) return;

        // Открываем селект с опорами
        // setSelectorAnchorEl(event?.currentTarget || document.body);
        setSelectorAnchorEl(progressRef.current)
        setShowPillarSelector(true);
    }, [proposedPillar]);

    // Функция закрытия селектора
    const closePillarSelector = useCallback(() => {
        setShowPillarSelector(false);
        setSelectorAnchorEl(null);
        // setProposedPillar(null);
    }, []);

    // Функция принудительного открытия селектора опор
    const forceOpenPillarSelector = useCallback((event) => {
        setSelectorAnchorEl(progressRef.current);
        setShowPillarSelector(true);
    }, []);

    // Функция создания маркера индикации опоры на карте
    const createPillarIndicatorMarker = useCallback((pillar, type) => {
        if (!pillar || !pillar.coords) return null;

        // Определяем цвет в зависимости от типа
        const getColor = () => {
            switch (type) {
                case 'proposed': return '#2196F3'; // Голубой для предлагаемой
                case 'current': return '#4CAF50';  // Зеленый для активной
                case 'visited': return '#9E9E9E';  // Серый для посещенных
                default: return '#2196F3';
            }
        };

        const color = getColor();

        const icon = new L.DivIcon({
            className: 'pillar-indicator-marker',
            iconSize: [40, 40],
            html: `<div style='position:absolute; bottom: 0; display:flex; margin-left: 0px; margin-bottom: 0px'>
                    <svg width="40" height="40" viewBox="0 0 100 100">
                        <circle r="35" cx="50" cy="50" fill="${color}" opacity="0.7" />
                    </svg>
                </div>`
        });

        const marker = L.marker(pillar.coords, {
            icon: icon,
            pane: 'shadowPane',
            zIndexOffset: -1000 // Помещаем под основные слои
        });

        return marker;
    }, []);

    // Функция очистки всех маркеров индикации
    const clearPillarIndicatorMarkers = useCallback(() => {
        // Очищаем предлагаемую опору
        if (pillarMarkersRef.current.proposed) {
            pillarMarkersRef.current.proposed.remove();
            pillarMarkersRef.current.proposed = null;
        }

        // Очищаем текущую опору
        if (pillarMarkersRef.current.current) {
            pillarMarkersRef.current.current.remove();
            pillarMarkersRef.current.current = null;
        }

        // Очищаем посещенные опоры
        pillarMarkersRef.current.visited.forEach(marker => {
            if (marker) marker.remove();
        });
        pillarMarkersRef.current.visited = [];
    }, []);

    // Функция обновления маркеров индикации на карте
    const updatePillarIndicatorMarkers = useCallback(() => {
        if (!enabled) return;

        // Сначала очищаем все существующие маркеры
        clearPillarIndicatorMarkers();

        // Добавляем маркер для предлагаемой опоры
        if (proposedPillar) {
            const marker = createPillarIndicatorMarker(proposedPillar.pillar, 'proposed');
            if (marker) {
                marker.addTo(map);
                pillarMarkersRef.current.proposed = marker;
            }
        }

        // Добавляем маркер для текущей опоры
        if (currentPillar) {
            const marker = createPillarIndicatorMarker(currentPillar.pillar, 'current');
            if (marker) {
                marker.addTo(map);
                pillarMarkersRef.current.current = marker;
            }
        }

        // Добавляем маркеры для посещенных опор
        visitedPillars.forEach(visitedPillar => {
            const marker = createPillarIndicatorMarker(visitedPillar.pillar, 'visited');
            if (marker) {
                marker.addTo(map);
                pillarMarkersRef.current.visited.push(marker);
            }
        });
    }, [enabled, proposedPillar, currentPillar, visitedPillars, map, createPillarIndicatorMarker, clearPillarIndicatorMarkers]);

    // Функция выбора опоры из селектора
    const selectPillarFromSelector = useCallback((pillar) => {
        if (!pillar) return;

        const newPillar = {
            pillar,
            distance: location ? new L.latLng(new L.latLng(pillar.coords[0], pillar.coords[1])).distanceTo(location.latlng) : 0,
            timestamp: Date.now(),
            status: 'confirmed'
        };

        // Если есть текущая опора, перемещаем её в историю
        if (currentPillar) {
            const existingIndex = visitedPillars.findIndex(p => p.pillar.fid === currentPillar.pillar.fid);
            if (existingIndex >= 0) {
                const updatedVisited = [...visitedPillars];
                updatedVisited[existingIndex] = currentPillar;
                setVisitedPillars(updatedVisited);
            } else {
                setVisitedPillars(prev => [currentPillar, ...prev]);
            }
        }

        // Делаем выбранную опору текущей
        setCurrentPillar(newPillar);
        setWorkPillar({
            pillar: pillar,
            time: newPillar.timestamp
        });

        closePillarSelector();
    }, [currentPillar, visitedPillars, location, closePillarSelector]);

    // Функция выбора исторической опоры как текущей
    const selectHistoricalPillar = useCallback((historicalPillar) => {
        if (!historicalPillar) return;

        // Если есть текущая опора, добавляем её в историю
        if (currentPillar) {
            const existingIndex = visitedPillars.findIndex(p => p.pillar.fid === currentPillar.pillar.fid);
            if (existingIndex >= 0) {
                const updatedVisited = [...visitedPillars];
                updatedVisited[existingIndex] = currentPillar;
                setVisitedPillars(updatedVisited);
            } else {
                setVisitedPillars(prev => [currentPillar, ...prev]);
            }
        }

        // Удаляем выбранную опору из истории
        setVisitedPillars(prev => prev.filter(p => p.pillar.fid !== historicalPillar.pillar.fid));

        // Обновляем timestamp и делаем её текущей
        const updatedPillar = {
            ...historicalPillar,
            timestamp: Date.now(),
            status: 'confirmed'
        };

        setCurrentPillar(updatedPillar);
        setWorkPillar({
            pillar: historicalPillar.pillar,
            time: updatedPillar.timestamp
        });
    }, [currentPillar, visitedPillars]);

    // Получение опор для селектора
    const getSelectorPillars = useCallback(() => {
        if (!location) return { nearby: [], within100m: [] };

        // Получаем опоры рядом (те которые были найдены алгоритмом)
        const counts = {};
        locatedPillars.flatMap(p => p.pillars).forEach(p => counts[p] = counts[p] ? counts[p] + 1 : 1);
        const nearbyPillarIds = Object.entries(counts)
            .filter(([key, value]) => value >= 2) // Смягчаем требование - хотя бы в 2 из 3 измерений
            .map(([key, value]) => Number(key));

        const nearbyPillars = nearbyPillarIds.map(fid => {
            const pillar = pillars.find(p => p.fid === fid);
            if (!pillar) return null;
            // const distance = calculateDistance(
            //     location.latitude,
            //     location.longitude,
            //     pillar.latitude,
            //     pillar.longitude
            // );
            const distance = new L.latLng(new L.latLng(pillar.coords[0], pillar.coords[1])).distanceTo(location.latlng)

            return { pillar, distance };
        }).filter(Boolean).sort((a, b) => a.distance - b.distance);

        // Получаем все опоры в радиусе 100м
        const within100mPillars = pillars.map(pillar => {
            // const distance = calculateDistance(
            //     location.latitude,
            //     location.longitude,
            //     pillar.latitude,
            //     pillar.longitude
            // );
            const distance = new L.latLng(new L.latLng(pillar.coords[0], pillar.coords[1])).distanceTo(location.latlng)
            return distance <= 100 ? { pillar, distance } : null;
        }).filter(Boolean)
            .filter(item => !nearbyPillarIds.includes(item.pillar.fid)) // Исключаем уже найденные рядом
            .sort((a, b) => a.distance - b.distance);

        return { nearby: nearbyPillars, within100m: within100mPillars };
    }, [location, locatedPillars, pillars]);

    const refreshIntervalRef = useRef(null)
    const syncQueueIntervalRef = useRef(null);

    // Состояние для long-press и анимации
    const [isLongPressing, setIsLongPressing] = useState(false)
    const longPressTimeoutRef = useRef(null)
    const touchStartTimeRef = useRef(null)
    const wasLongPressRef = useRef(false)
    const progressRef = useRef(null)

    // Состояние для popover
    const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)

    // Определяем нужно ли показывать элементы (новая логика)
    const shouldShowElements = useMemo(() => {
        return proposedPillar || currentPillar || visitedPillars.length > 0
    }, [proposedPillar, currentPillar, visitedPillars])

    // Определяем нужно ли привлечь внимание пользователя (новая логика)
    const needsAttention = useMemo(() => {
        return proposedPillar && proposedPillar.status === 'pending'
    }, [proposedPillar])

    // Обработчики для long-press
    const handleLongPressStart = useCallback((e) => {
        // Пропускаем дублирующиеся события
        if (isLongPressing) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        // Предотвращаем контекстное меню и выделение текста
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none';
        document.body.style.webkitTouchCallout = 'none';

        setIsLongPressing(true);
        touchStartTimeRef.current = Date.now();

        console.log('Long press start:', e.type);

        // Добавляем класс анимации к контейнеру
        if (progressRef.current) {
            progressRef.current.classList.add('long-press-animation');
        }

        longPressTimeoutRef.current = setTimeout(() => {
            console.log('Long press completed, toggling location');
            wasLongPressRef.current = true;
            setEnabled(prev => !prev);
            setIsLongPressing(false);

            // Убираем класс анимации
            if (progressRef.current) {
                progressRef.current.classList.remove('long-press-animation');
            }

            // Возвращаем выделение текста
            document.body.style.userSelect = '';
            document.body.style.webkitUserSelect = '';
            document.body.style.webkitTouchCallout = '';
        }, 2000);
    }, [isLongPressing]);

    const handleLongPressEnd = useCallback((e) => {
        if (!isLongPressing) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        console.log('Long press end:', e.type);

        if (longPressTimeoutRef.current) {
            clearTimeout(longPressTimeoutRef.current);
            longPressTimeoutRef.current = null;
        }

        setIsLongPressing(false);

        // Убираем класс анимации
        if (progressRef.current) {
            progressRef.current.classList.remove('long-press-animation');
        }

        // Возвращаем выделение текста
        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';
        document.body.style.webkitTouchCallout = '';
    }, [isLongPressing]);

    const handleContextMenu = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
    }, []);

    const handleClick = useCallback((e) => {
        // Проверяем, было ли это долгое нажатие
        if (wasLongPressRef.current) {
            wasLongPressRef.current = false;
            return false;
        }

        e.preventDefault();
        e.stopPropagation();

        // Если геолокация активна и у нас есть координаты, центрируем карту
        if (enabled && location?.latitude && location?.longitude) {
            console.log('Centering map to current location:', location.latitude, location.longitude);

            // Создаем границы на основе точности для плавного зума
            let targetBounds;
            if (location.accuracy < 5) {
                // Высокая точность - используем радиус 5м
                const tempCircle = L.circle([location.latitude, location.longitude], { radius: 5 });
                targetBounds = tempCircle.getBounds();
            } else if (location.accuracy >= 5 && location.accuracy < 30) {
                // Средняя точность - используем радиус точности
                const tempCircle = L.circle([location.latitude, location.longitude], { radius: location.accuracy });
                targetBounds = tempCircle.getBounds();
            } else {
                // Низкая точность - просто центрируем без изменения зума
                map.flyTo([location.latitude, location.longitude], map.getZoom(), {
                    animate: true,
                    duration: 1.5
                });
                return false;
            }

            // Плавное перемещение к границам
            map.flyToBounds(targetBounds, {
                maxZoom: 18,
                animate: true,
                duration: 1.5,
                easeLinearity: 0.25
            });
        }

        return false;
    }, [enabled, location, map]);


    // Эффект обновления маркеров индикации опор
    useEffect(() => {
        updatePillarIndicatorMarkers();
    }, [updatePillarIndicatorMarkers]);

    // Эффект очистки состояний при отключении геолокации
    useEffect(() => {
        if (!enabled) {
            setProposedPillar(null);
            setCurrentPillar(null);
            setVisitedPillars([]);
            setShowPillarSelector(false);
            setSelectorAnchorEl(null);
            // Очищаем маркеры индикации
            clearPillarIndicatorMarkers();
        }
    }, [enabled, clearPillarIndicatorMarkers])

    // Helper functions for localStorage queue
    const getQueue = useCallback(() => {
        try {
            return JSON.parse(localStorage.getItem(WORK_PILLAR_LOG_QUEUE_KEY)) || [];
        } catch (e) {
            console.error("Failed to parse work pillar log queue", e);
            return [];
        }
    }, []);

    const setQueue = useCallback((queue) => {
        try {
            localStorage.setItem(WORK_PILLAR_LOG_QUEUE_KEY, JSON.stringify(queue));
        } catch (e) {
            console.error("Failed to set work pillar log queue", e);
        }
    }, []);

    // Function to process the offline queue
    const processQueue = useCallback(async () => {
        const queue = getQueue();
        if (queue.length === 0) {
            return;
        }

        console.log(`Processing work pillar log queue, ${queue.length} items...`);

        let updatedQueue = [...queue];

        for (const item of queue) {
            try {
                const response = await $authHost.post('/apig/field-works', item);
                const data = response.data;
                if (!data.success) {
                    throw new Error(data.error || 'Не удалось записать рабочую опору из очереди');
                }
                console.log('Work pillar logged from queue:', data.data);
                // Remove successful item from queue
                updatedQueue = updatedQueue.filter(q => q.dt !== item.dt);
            } catch (error) {
                console.error('Ошибка записи рабочей опоры из очереди:', error.message);
                // Keep item in queue on failure
            }
        }
        setQueue(updatedQueue);
    }, [getQueue, setQueue]);

    // Interval to process the queue
    useEffect(() => {
        if (enabled) {
            // Immediately try to process queue on enable
            processQueue();

            syncQueueIntervalRef.current = setInterval(() => {
                processQueue();
            }, 30000); // 30 seconds
        } else {
            if (syncQueueIntervalRef.current) {
                clearInterval(syncQueueIntervalRef.current);
                syncQueueIntervalRef.current = null;
            }
        }

        return () => {
            if (syncQueueIntervalRef.current) {
                clearInterval(syncQueueIntervalRef.current);
                syncQueueIntervalRef.current = null;
            }
        };
    }, [enabled, processQueue]);

    useMapEvents({
        locationfound: (e) => {
            console.log('Location found:', e);
            setLocation(e);

            if (e.accuracy > 20) {
                setError({ message: `Высокая погрешность: ${e.accuracy.toFixed(0)} м`, code: 'custom' });
            } else {
                setError(null);
            }

            let boundsToFire = null;
            locatedPillarsRef.current = []

            if (e.accuracy < 5) {
                const tempCircle = L.circle(e.latlng, { radius: 5 }).addTo(map);
                boundsToFire = tempCircle.getBounds();
                tempCircle.removeFrom(map);
            } else if (e.accuracy >= 5 && e.accuracy < 30) {
                boundsToFire = e.bounds;
            }

            // if (boundsToFire) {



            if (boundsToFire) {
                map.flyToBounds(boundsToFire, {
                    maxZoom: 18,
                    animate: true
                })
                map.fire('locationbounds', { bounds: boundsToFire, timestamp: new Date().getTime() })
                // Для тестирования используем фиксированные координаты
                // map.fire('locationbounds', { bounds: new L.latLngBounds(new L.latLng(42.178043, 47.791585), new L.latLng(42.175555, 47.794477)), timestamp: new Date().getTime() })
            }


        },
        locationerror: (e) => {
            console.log('Location error:', e)
            setError(e)
            // Для тестирования используем те же координаты
            // map.fire('locationbounds', { bounds: new L.latLngBounds(new L.latLng(42.178043, 47.791585), new L.latLng(42.175555, 47.794477)), timestamp: new Date().getTime() })
        },
        locationpillars: (e) => {
            console.log('Received pillars:', e)
            locatedPillarsRef.current = [...locatedPillarsRef.current, ...e.pillars]
            console.log(locatedPillarsRef.current)

            if (locatedPillarsTimeoutRef.current) {
                clearTimeout(locatedPillarsTimeoutRef.current)
                locatedPillarsTimeoutRef.current = null
            }
            locatedPillarsTimeoutRef.current = setTimeout(() => {
                setLocatedPillars(prev => ([...prev, { timestamp: e.timestamp, pillars: locatedPillarsRef.current }]))
            }, 3000)
        },
    })

    useEffect(() => {
        if (locatedPillars.length < 3) return
        const counts = {}
        locatedPillars.flatMap(p => p.pillars).forEach(p => counts[p] = counts[p] ? counts[p] + 1 : 1)
        const pillarsIds = Object.entries(counts).filter(([key, value]) => value === locatedPillars.length).map(([key, value]) => Number(key))
        console.log('Pillars found nearby for 30+ seconds:', pillarsIds)

        if (pillarsIds.length > 0) {
            // Получаем данные опор и вычисляем расстояние до них
            const nearbyPillars = pillarsIds.map(fid => {
                const pillar = pillars.find(p => p.fid === fid)
                if (!pillar || !location) return null

                // Вычисляем расстояние от текущей позиции до опоры
                // const distance = calculateDistance(
                //     location.latitude,
                //     location.longitude,
                //     pillar.latitude,
                //     pillar.longitude
                // )

                const distance = new L.latLng(new L.latLng(pillar.coords[0], pillar.coords[1])).distanceTo(location.latlng)
                console.log(distance)
                return {
                    pillar,
                    distance,
                    fid
                }
            }).filter(Boolean).sort((a, b) => a.distance - b.distance) // Сортируем по близости

            console.log('Sorted nearby pillars:', nearbyPillars)

            // Здесь будет логика обработки найденных опор
            processNearbyPillars(nearbyPillars)
        }
    }, [locatedPillars, pillars, location])

    // Effect for sending workPillar data
    useEffect(() => {
        if (!workPillar || !user?.id) {
            return;
        }

        const logData = {
            action: 'handle',
            transformer: Number(workPillar.pillar.tp_id),
            pillar: workPillar.pillar.fid,
            user_id: user.id,
            dt: new Date(workPillar.time).toISOString()
        };

        const sendLog = async () => {
            try {
                setWorkPillarError(false);
                const response = await $authHost.post('/apig/field-works', logData);
                const data = response.data;
                if (!data.success) {
                    throw new Error(data.error || 'Не удалось записать рабочую опору');
                }
                console.log('Work pillar logged:', data.data);
            } catch (error) {
                console.error('Ошибка записи рабочей опоры:', error);
                // alert(`Ошибка записи рабочей опоры в базу. Данные сохранены для последующей отправки. Ошибка: ${error.message}`);
                setWorkPillarError(true);

                // Add to queue on failure
                const queue = getQueue();
                // Avoid duplicates by checking timestamp
                if (!queue.some(item => item.dt === logData.dt)) {
                    setQueue([...queue, logData]);
                }
            }
        };

        sendLog();

    }, [workPillar, user, getQueue, setQueue]);

    useEffect(() => {
        let isSubscribed = true;

        const handleOrientation = (e) => {
            const heading = e.webkitCompassHeading || e.alpha;
            if (heading !== null) {
                setOrientation(heading);
            }
        };

        const eventName = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';

        if (enabled) {
            if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
                DeviceOrientationEvent.requestPermission()
                    .then(permissionState => {
                        if (permissionState === 'granted' && isSubscribed) {
                            window.addEventListener(eventName, handleOrientation, true);
                        }
                    })
                    .catch(console.error);
            } else {
                window.addEventListener(eventName, handleOrientation, true);
            }
        } else {
            setOrientation(null);
        }

        return () => {
            isSubscribed = false;
            window.removeEventListener(eventName, handleOrientation, true);
        };
    }, [enabled]);

    useEffect(() => {
        // Очищаем предыдущий интервал если есть
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current)
            refreshIntervalRef.current = null
        }

        if (enabled) {
            console.log('Starting geolocation with 10s interval')
            map.locate({
                watch: false,
                // setView: true,
                enableHighAccuracy: true,
            })
            refreshIntervalRef.current = setInterval(() => {
                console.log('Geolocation interval tick')
                map.locate({
                    watch: false,
                    // setView: true,
                    enableHighAccuracy: true,
                })
                console.log()
                setLocatedPillars(prev => prev.filter(p => new Date().getTime() < p.timestamp + 20000))
            }, 5000)
        } else {
            setWorkPillar(null)
            setLocation(null)
            setError(null)
            setPopoverAnchorEl(null)
            setLocatedPillars([])
        }

        return () => {
            if (refreshIntervalRef.current) {
                console.log('Clearing geolocation interval')
                clearInterval(refreshIntervalRef.current)
                refreshIntervalRef.current = null
            }
        }
    }, [enabled])

    useEffect(() => {
        try {
            if (location?.latitude && location?.longitude && enabled) {
                if (marker.current) {
                    marker.current.setLatLng([location.latitude, location.longitude])
                } else {
                    marker.current = L.marker([location.latitude, location.longitude], {
                        icon: new L.DivIcon({
                            className: 'leaflet-simple-locate-icon',
                            html: `
                            <svg width="24" height="24" viewBox="-14 -14 28 28" xmlns="http://www.w3.org/2000/svg">
                                <defs>
                                    <filter id="gaussian">
                                        <feGaussianBlur stdDeviation="0.5"></feGaussianBlur>
                                    </filter>
                                </defs>
                                <g id="direction-arrow">
                                    <path d="M 0 -14 L 7 -5 L -7 -5 Z" fill="#c00000"></path>
                                </g>
                                <g id="leaflet-simple-locate-icon-spot">
                                    <circle fill="#22B571" style="opacity:0.3;filter:url(#gaussian)" cx="0" cy="0" r="10"></circle>
                                    <circle fill="#ffffff" r="9"></circle>
                                    <circle r="5" fill="#22B571">
                                        <animate attributeName="r" values="5;7;5" dur="2s" repeatCount="indefinite"></animate>
                                    </circle>
                                </g>
                            </svg>`,
                            iconSize: [24, 24],
                            iconAnchor: [12, 12],
                        })
                    }).addTo(map)
                }
                if (markerAccuracy.current) {
                    markerAccuracy.current.setLatLng([location.latitude, location.longitude])
                } else {
                    markerAccuracy.current = L.circle([location.latitude, location.longitude], {
                        color: '#22B571',
                        fillColor: '#22B571',
                        fillOpacity: 0.1,
                        weight: 1,
                        opacity: 0.7,
                        radius: location.accuracy
                    }).addTo(map)
                }
            } else {
                if (marker.current) {
                    marker.current.remove()
                    marker.current = null
                }
                if (markerAccuracy.current) {
                    markerAccuracy.current.remove()
                    markerAccuracy.current = null
                }
            }
        } catch (e) {
            console.log(e)
        }
    }, [location, enabled])

    useEffect(() => {
        if (marker.current && orientation !== null) {
            const element = marker.current.getElement();
            if (element) {
                const arrow = element.querySelector('#direction-arrow');
                if (arrow) {
                    arrow.setAttribute('transform', `rotate(${-orientation})`);
                }
            }
        }
    }, [orientation]);

    useEffect(() => {
        if (buttonRef.current) {
            L.DomEvent.disableClickPropagation(buttonRef.current)
            L.DomEvent.disableScrollPropagation(buttonRef.current)
        }
    }, [buttonRef.current])

    // Cleanup effect для long-press timeout и маркеров
    useEffect(() => {
        return () => {
            if (longPressTimeoutRef.current) {
                clearTimeout(longPressTimeoutRef.current);
                longPressTimeoutRef.current = null;
            }
            wasLongPressRef.current = false;

            // Очищаем анимацию при размонтировании
            if (progressRef.current) {
                progressRef.current.classList.remove('long-press-animation');
            }

            // Очищаем все маркеры индикации при размонтировании
            clearPillarIndicatorMarkers();
        };
    }, [clearPillarIndicatorMarkers]);

    // Мемоизированные колбэки
    const formatTime = useCallback((timestamp) => {
        return new Date(timestamp).toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }, []);


    // Компонент для отображения информации о геолокации
    const LocationInfo = useMemo(() => {
        if (!enabled || !location) return null;

        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                padding: '5px 5px 5px 0',
                // px: 1,
                // py: 0.5,
                bgcolor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: 0.5,
                width: 'fit-content',
                // minWidth: '150px',
                // maxWidth: '250px'
            }}>
                {/* Координаты */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MyLocationIcon sx={{ fontSize: { xs: 10, md: 12 }, color: '#22B571' }} />
                    <Typography sx={{
                        fontSize: { xs: '0.6rem', md: '0.7rem' },
                        color: 'white',
                        // fontFamily: 'monospace',
                        lineHeight: 1.1
                    }}
                        onClick={(e) => {
                            e.stopPropagation();
                            try {
                                const coordinates = `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`;
                                navigator.clipboard.writeText(coordinates);
                                console.log('Координаты скопированы в буфер обмена:', coordinates);
                                // Можно добавить toast уведомление
                            } catch (err) {
                                console.error('Ошибка копирования в буфер обмена:', err);
                                // Fallback для старых браузеров
                                const textArea = document.createElement('textarea');
                                textArea.value = `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                            }
                        }}
                    >
                        {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                    </Typography>
                </Box>

                {/* Точность и время в одной строке */}
                {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> */}
                {/* Точность */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <GpsFixedIcon sx={{
                        fontSize: { xs: 10, md: 12 },
                        color: location.accuracy < 5 ? '#4caf50' : location.accuracy < 20 ? '#ff9800' : '#f44336'
                    }} />
                    <Typography sx={{
                        fontSize: { xs: '0.6rem', md: '0.7rem' },
                        color: location.accuracy < 5 ? '#4caf50' : location.accuracy < 20 ? '#ff9800' : '#f44336',
                        fontWeight: 500
                    }}>
                        ±{location.accuracy.toFixed(0)}м
                    </Typography>
                </Box>

                {/* Время */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <ScheduleIcon sx={{ fontSize: { xs: 10, md: 12 }, color: '#90caf9' }} />
                    <Typography sx={{
                        fontSize: { xs: '0.6rem', md: '0.7rem' },
                        color: '#90caf9'
                    }}>
                        {new Date(location.timestamp).toLocaleTimeString('ru-RU', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                        })}
                    </Typography>
                </Box>
                {/* </Box> */}

                {/* Направление если есть */}
                {orientation !== null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <ExploreIcon sx={{
                            fontSize: { xs: 10, md: 12 },
                            color: '#ff9800',
                            transform: `rotate(${orientation}deg)`
                        }} />
                        <Typography sx={{
                            fontSize: { xs: '0.6rem', md: '0.7rem' },
                            color: '#ff9800'
                        }}>
                            {orientation.toFixed(0)}°
                        </Typography>
                    </Box>
                )}

                {/* Подсказки управления */}
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, mt: 0.3, pt: 0.3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <TouchAppIcon sx={{ fontSize: 9, color: '#90caf9' }} />
                        <Typography sx={{ fontSize: '0.55rem', color: '#90caf9' }}>
                            Клик - центрировать
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <TouchAppIcon sx={{ fontSize: 9, color: '#90caf9' }} />
                        <Typography sx={{ fontSize: '0.55rem', color: '#90caf9' }}>
                            Удержание 2с - выключить
                        </Typography>
                    </Box>
                </Box> */}
            </Box>
        );
    }, [enabled, location, orientation]);


    // Компонент для отображения списка опор
    const PillarCardsContainer = useMemo(() => {
        // Если нет никаких опор, не показываем контейнер
        if (!proposedPillar && !currentPillar && visitedPillars.length === 0) {

            return !enabled ? null : <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 500,
                px: 1,
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
 
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 1
                }}>
                    <Box sx={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid #2196F3',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                        }
                    }} />
                    <Typography sx={{
                        fontSize: { xs: '13px', md: '14px' },
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                        // animation: 'pulse 1.5s ease-in-out infinite',
                        // '@keyframes pulse': {
                        //     '0%, 100%': { opacity: 0.8 },
                        //     '50%': { opacity: 1 }
                        // }
                    }}>
                        Идет поиск опор...
                    </Typography>
                </Box>
            </Box>;
        }

        // Формируем список опор в нужном порядке
        const pillars = [];

        // 1. Предлагаемая опора (голубой фон) - всегда сверху
        if (proposedPillar) {
            pillars.push({
                ...proposedPillar,
                type: 'proposed'
            });
        }

        // 2. Текущая опора (зеленый фон)
        if (currentPillar) {
            pillars.push({
                ...currentPillar,
                type: 'current'
            });
        }

        // 3. Исторические опоры (серый фон) - по времени, новые сверху
        visitedPillars.forEach(pillar => {
            pillars.push({
                ...pillar,
                type: 'historical'
            });
        });

        // Вычисляем высоту контейнера на основе количества элементов
        const getContainerHeight = () => {
            const maxVisibleItems = 3;
            const itemHeight = window.innerWidth < 768 ? 28 : 34; // примерная высота одного элемента
            const gap = 2; // gap между элементами в пикселях (0.2 * 8 = 1.6, округляем до 2)
            const padding = 3.2; // padding контейнера в пикселях (0.2 * 8 * 2 = 3.2)

            const visibleItems = Math.min(pillars.length, maxVisibleItems);
            return (visibleItems * itemHeight) + ((visibleItems - 1) * gap) + padding;
        };

        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.2,
                height: `${getContainerHeight()}px`,
                maxHeight: `${getContainerHeight()}px`,
                overflowY: pillars.length > 3 ? 'auto' : 'visible',
                width: 'fit-content',
                // minWidth: '200px',
                maxWidth: '320px',
                bgcolor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: 1,
                p: 0.2,

                backdropFilter: 'blur(5px)',
                // border: '1px solid rgba(255, 255, 255, 0.1)',
                // Стили скроллбара
                '&::-webkit-scrollbar': {
                    width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '2px',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    },
                },
                // Анимация появления
                animation: 'fadeInUp 0.3s ease-out',
                '@keyframes fadeInUp': {
                    '0%': {
                        opacity: 0,
                        transform: 'translateY(10px)',
                    },
                    '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                    },
                },
            }}>
                {pillars.map((item, index) => (
                    <PillarCard
                        key={`${item.type}-${item.pillar.fid}-${item.timestamp}`}
                        pillar={item.pillar}
                        status={item.type === 'proposed' ? 'pending' :
                            item.type === 'current' ? 'confirmed' : 'inactive'}
                        distance={item.distance}
                        timestamp={item.timestamp}
                        onConfirm={item.type === 'proposed' ? confirmProposedPillar : undefined}
                        onReject={item.type === 'proposed' ? rejectProposedPillar : undefined}
                        onForceSelector={item.type === 'current' ? forceOpenPillarSelector : undefined}
                        onClick={() => {
                            // Всегда показываем опору на карте при клике
                            aimToObject(map, item.pillar);
                            // Для исторических опор только показываем на карте, не активируем
                        }}
                        onLongPress={() => {
                            // Долгий тап для активации исторической опоры
                            if (item.type === 'historical') {
                                selectHistoricalPillar(item);
                            }
                        }}
                        isActive={item.type === 'current'}
                        isPrimary={item.type === 'proposed' || item.type === 'current'}
                    />
                ))}
            </Box>
        );
    }, [proposedPillar, currentPillar, visitedPillars, confirmProposedPillar, rejectProposedPillar, selectHistoricalPillar, forceOpenPillarSelector, map, enabled]);


    return createPortal(
        <Box sx={{
            zIndex: 1202,
            display: 'flex',
            gap: 0.5,
            order: 5,
            pointerEvents: 'auto',
            width: 'fit-content',
            alignItems: 'center',
            opacity: 0.9,
            marginTop: enabled ? '10px' : '0',
        }}>
            {/* Расширяемый контейнер для кнопки и информации */}
            <Paper
                elevation={3}
                ref={progressRef}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'rgb(40, 40, 40)',
                    borderRadius: 1,
                    overflow: 'hidden',
                    transition: 'width 0.3s ease-in-out',
                    width: enabled && location ? ({ xs: '95vw', md: 'auto' }) : ({ xs: 26, md: 30 }),
                    minWidth: { xs: 26, md: 30 },
                    maxWidth: { xs: '95vw', md: 'auto' },
                    boxShadow: needsAttention ? '0px 0px 15px 3px rgba(255, 165, 0, 0.8)' : '0px 2px 6px grey',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* Кнопка геолокации */}
                    <Tooltip title={!enabled ?
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            <Typography sx={{ fontSize: { xs: 10, sm: 12 } }}>Начать работу</Typography>
                            <Typography sx={{ fontSize: { xs: 9, sm: 11 }, color: '#90caf9' }}>Удержание 2с - включить</Typography>
                        </Box> : ''
                    } placement='right' disableTouchListener={true} arrow disabled={enabled && location}>
                        <Box
                            ref={buttonRef}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: { xs: 26, md: 30 },
                                height: { xs: 26, md: 30 },
                                color: 'rgb(0, 174, 255)',
                                cursor: 'pointer',
                                transition: 'transform 0.3s ease-in-out',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                WebkitTouchCallout: 'none',
                                touchAction: 'manipulation',
                                flexShrink: 0,
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                },
                            }}
                            onClick={handleClick}
                            onMouseDown={handleLongPressStart}
                            onMouseUp={handleLongPressEnd}
                            onMouseLeave={handleLongPressEnd}
                            onTouchStart={handleLongPressStart}
                            onTouchEnd={handleLongPressEnd}
                            onTouchCancel={handleLongPressEnd}
                            onContextMenu={handleContextMenu}
                            onPointerDown={handleLongPressStart}
                            onPointerUp={handleLongPressEnd}
                            onPointerLeave={handleLongPressEnd}
                            onPointerCancel={handleLongPressEnd}
                        >
                            {!enabled ? (
                                // <svg width={window.innerWidth < 768 ? 19 : 24} height={window.innerWidth < 768 ? 19 : 24} viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill='#22B571'>
                                //     <path d="M 8,1.5 A 6.5,6.5 0 0 0 1.5,8 6.5,6.5 0 0 0 8,14.5 6.5,6.5 0 0 0 14.5,8 6.5,6.5 0 0 0 8,1.5 Z m 0,2 A 4.5,4.5 0 0 1 12.5,8 4.5,4.5 0 0 1 8,12.5 4.5,4.5 0 0 1 3.5,8 4.5,4.5 0 0 1 8,3.5 Z"></path>
                                //     <rect width="1.5" height="4" x="7.25" y="0.5" rx="0.5" ry="0.5"></rect>
                                //     <rect width="1.5" height="4" x="7.25" y="11.5" rx="0.5" ry="0.5"></rect>
                                //     <rect width="4" height="1.5" x="0.5" y="7.25" rx="0.5" ry="0.5"></rect>
                                //     <rect width="4" height="1.5" x="11.5" y="7.25" ry="0.5" rx="0.5"></rect>
                                //     <circle cx="8" cy="8" r="1"></circle>
                                // </svg>
                                <EngineeringIcon sx={{ color: '#8baa64', fontSize: { xs: 19, md: 24 } }} />
                            ) : (
                                <svg
                                    className="leaflet-simple-locate-orientation"
                                    width={window.innerWidth < 768 ? 19 : 24}
                                    height={window.innerWidth < 768 ? 19 : 24}
                                    viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"
                                    style={{ transform: orientation !== null ? `rotate(${-orientation}deg)` : undefined, transformOrigin: 'center' }}
                                >
                                    <path d="M 8,0 C 7.7058986,0 7.4109021,0.30139625 7.1855469,0.90234375 L 5.3378906,5.8300781 C 5.2559225,6.0486598 5.1908259,6.292468 5.1386719,6.5507812 6.0506884,6.193573 7.0205489,6.0068832 8,6 8.9768002,6.0005071 9.945249,6.1798985 10.857422,6.5292969 10.805917,6.2790667 10.741782,6.0425374 10.662109,5.8300781 L 8.8144531,0.90234375 C 8.5890978,0.30139615 8.2941007,0 8,0 Z"
                                        fill="#c00000"
                                    ></path>
                                    <path d="M 8,5.9999998 C 7.0205501,6.006884 6.0506874,6.1935733 5.138672,6.5507817 4.9040515,7.7126196 4.9691485,9.1866095 5.3378906,10.169922 l 1.8476563,4.927734 c 0.4507105,1.201895 1.1781958,1.201894 1.628906,0 L 10.662109,10.169922 C 11.033147,9.1804875 11.097283,7.6944254 10.857422,6.5292967 9.9452497,6.1798989 8.9767993,6.0005076 8,5.9999998 Z m -1e-7,0.7499999 A 1.25,1.258 90 0 1 9.2578124,7.9999996 1.25,1.258 90 0 1 8,9.2500001 a 1.25,1.258 90 0 1 -1.2578124,-1.25 1.25,1.258 90 0 1 1.2578123,-1.2500004 z"
                                        fill="#22B571"
                                    ></path>
                                </svg>
                            )}
                        </Box>
                    </Tooltip>

                    {/* Информация о геолокации (показывается только когда активна) */}
                    {LocationInfo}
                </Box>
                {PillarCardsContainer}
            </Paper>

            {/* Отдельный контейнер для плашек опор */}


            {/* Badge с ошибкой вне Tooltip */}
            {/* {error && (
                <Box sx={{ position: 'absolute', display: 'inline-block' }}>
                    <Tooltip title={error?.message || 'Ошибка определения местоположения'} placement='right' enterTouchDelay={0}>
                        <Badge
                            badgeContent={<SatelliteAltOutlinedIcon sx={{ fontSize: window.innerWidth < 768 ? 11 : 13, color: '#ffffff' }} />}
                            color="secondary"
                            max={999}
                            variant="standard"
                            sx={{
                                position: 'absolute',
                                bottom: window.innerWidth < 768 ? -14 : -16,
                                left: window.innerWidth < 768 ? 2 : 2,
                                '& .MuiBadge-badge': {
                                    color: 'black',
                                    backgroundColor: error?.code === 'custom' ? '#ff9800' : '#c00000',
                                    fontSize: window.innerWidth < 768 ? 9 : 12,
                                    padding: '2px',
                                    width: 'auto',
                                    height: 'auto',
                                }
                            }}
                        />
                    </Tooltip>
                </Box>
            )} */}

            {/* Badge с ошибкой отправки лога */}
            {workPillarError && (
                <Box sx={{ position: 'absolute', display: 'inline-block' }}>
                    <Tooltip title="Ошибка отправки данных на сервер. Данные будут отправлены позже." placement='right' enterTouchDelay={0}>
                        <Badge
                            badgeContent={<CloudOffIcon sx={{ fontSize: window.innerWidth < 768 ? 10 : 12, color: '#ffffff' }} />}
                            color="error"
                            variant="standard"
                            sx={{
                                position: 'absolute',
                                top: window.innerWidth < 768 ? -14 : -16,
                                left: window.innerWidth < 768 ? 2 : 2,
                                '& .MuiBadge-badge': {
                                    color: 'black',
                                    backgroundColor: '#f44336',
                                    fontSize: window.innerWidth < 768 ? 9 : 12,
                                    padding: '2px',
                                    width: 'auto',
                                    height: 'auto',
                                }

                            }}
                        />
                    </Tooltip>
                </Box>
            )}

            {/* Popover селектора опор */}
            <Popover
                open={showPillarSelector}
                anchorEl={selectorAnchorEl}
                onClose={closePillarSelector}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: 'rgb(40, 40, 40)',
                            color: 'white',
                            border: '1px solid rgb(80, 80, 80)',
                            maxWidth: 400,
                            maxHeight: 400,
                        }
                    }
                }}
            >
                <PillarSelector
                    getSelectorPillars={getSelectorPillars}
                    onSelect={selectPillarFromSelector}
                    onClose={closePillarSelector}
                />
            </Popover>
        </Box>
        , document.getElementById(window.innerWidth < 768 ? (enabled ? "map-controls-left-bottom-second-row" : "map-controls-left-bottom-first-row-left") : "map-controls-left-bottom")
    )
};

export default LocationControl;
